import React, { useState } from 'react';
import { ChevronDown, ChevronUp, BookOpen, Lightbulb, AlertTriangle, CheckCircle } from 'lucide-react';
import { instructions, jediInstructions, postDraftChecklist, type InstructionLayer } from './instructions';

interface InstructionPanelProps {
  defaultLayer?: InstructionLayer;
  showJedi?: boolean;
  showChecklist?: boolean;
  className?: string;
}

const LAYER_LABELS: Record<InstructionLayer, string> = {
  beginner: 'Beginner',
  practitioner: 'Practitioner',
  expert: 'Expert',
};

export const InstructionPanel: React.FC<InstructionPanelProps> = ({
  defaultLayer = 'beginner',
  showJedi = true,
  showChecklist = true,
  className = '',
}) => {
  const [activeLayer, setActiveLayer] = useState<InstructionLayer>(defaultLayer);
  const [showJediVersion, setShowJediVersion] = useState(false);
  const [showChecklistSection, setShowChecklistSection] = useState(false);

  const content = instructions[activeLayer];

  return (
    <div
      className={`bg-[var(--bg-raised)]  border border-[var(--rule)] overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-4 border-b border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/60">
        <BookOpen className="h-4 w-4 text-primary-500 dark:text-primary-400 flex-shrink-0" />
        <h2 className="text-sm font-semibold text-[var(--text-pri)]">Instructions</h2>
      </div>

      {/* Layer tabs */}
      <div className="flex border-b border-[var(--rule)] bg-[var(--bg-raised)]">
        {(Object.keys(LAYER_LABELS) as InstructionLayer[]).map((layer) => (
          <button
            key={layer}
            onClick={() => setActiveLayer(layer)}
            className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors ${
              activeLayer === layer
                ? 'bg-primary-600 text-white'
                : 'text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-ter)]'
            }`}
          >
            {LAYER_LABELS[layer]}
          </button>
        ))}
      </div>

      {/* Active layer content */}
      <div className="px-5 py-4 space-y-5 bg-[var(--bg-raised)]">
        {/* Title + subtitle */}
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">{content.title}</h3>
          <p className="text-xs text-[var(--text-ter)] italic mt-0.5">{content.subtitle}</p>
        </div>

        {/* What Is */}
        <div>
          <h4 className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide mb-1.5">
            {content.whatIs.title}
          </h4>
          <p className="text-xs text-[var(--text-sec)] leading-relaxed">
            {content.whatIs.content}
          </p>
        </div>

        {/* How To Write */}
        <div>
          <h4 className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide mb-2">
            {content.howToWrite.title}
          </h4>
          <div className="space-y-3">
            {content.howToWrite.sections.map((section, idx) => (
              <div key={idx} className="pl-3 border-l-2 border-primary-200 dark:border-primary-800">
                <h5 className="text-xs font-medium text-[var(--text-pri)] mb-1">
                  {section.title}
                </h5>
                <ul className="space-y-1">
                  {section.content.map((item, itemIdx) => (
                    <li key={itemIdx} className="text-xs text-[var(--text-sec)] flex items-start gap-1.5">
                      <span className="text-primary-500 dark:text-primary-400 mt-0.5 flex-shrink-0">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Tips */}
        {content.tips && content.tips.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Lightbulb className="h-3 w-3 text-primary-500 dark:text-primary-400" />
              Tips
            </h4>
            <ul className="space-y-1">
              {content.tips.map((tip, idx) => (
                <li key={idx} className="text-xs text-[var(--text-sec)] flex items-start gap-1.5">
                  <CheckCircle className="h-3 w-3 text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pitfalls */}
        {content.pitfalls && content.pitfalls.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3 text-[var(--mustard)]" />
              Pitfalls
            </h4>
            <ul className="space-y-1">
              {content.pitfalls.map((pitfall, idx) => (
                <li key={idx} className="text-xs text-[var(--text-sec)] flex items-start gap-1.5">
                  <span className="text-[var(--mustard)] mt-0.5 flex-shrink-0">•</span>
                  <span>{pitfall}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Jedi Version — collapsible */}
      {showJedi && (
        <div className="border-t border-[var(--rule)] bg-[var(--bg-raised)]">
          <button
            onClick={() => setShowJediVersion(!showJediVersion)}
            className="flex items-center justify-between w-full px-5 py-3 text-left hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/60 transition-colors"
          >
            <span className="text-xs font-semibold text-[var(--text-sec)]">Jedi Version</span>
            {showJediVersion ? (
              <ChevronUp className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            )}
          </button>
          {showJediVersion && (
            <div className="px-5 pb-4 space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-[var(--text-sec)] mb-2">Jedi Buckets</h4>
                <div className="space-y-2 text-xs">
                  {(['current-state', 'problem-opportunity', 'ramifications'] as const).map((key) => (
                    <div key={key}>
                      <span className="font-medium text-[var(--text-pri)]">
                        {key === 'current-state' ? 'Current State' : key === 'problem-opportunity' ? 'Problem / Opportunity' : 'Ramifications'}:{' '}
                      </span>
                      <span className="text-[var(--text-sec)] italic">{jediInstructions.buckets[key]}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-[var(--text-sec)] mb-2">Jedi Rules</h4>
                <ul className="space-y-1">
                  {jediInstructions.rules.map((rule, idx) => (
                    <li key={idx} className="text-xs text-[var(--text-sec)] flex items-start gap-1.5">
                      <span className="text-primary-500 dark:text-primary-400 flex-shrink-0">•</span>
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-[var(--text-sec)] mb-2">Jedi Examples</h4>
                <div className="space-y-2 text-xs">
                  <div>
                    <span className="font-medium text-[var(--text-pri)]">Current State: </span>
                    <span className="text-[var(--text-sec)]">"{jediInstructions.examples['current-state']}"</span>
                  </div>
                  <div>
                    <span className="font-medium text-[var(--text-pri)]">Problem: </span>
                    <span className="text-[var(--text-sec)]">"{jediInstructions.examples['problem-opportunity']}"</span>
                  </div>
                  <div>
                    <span className="font-medium text-[var(--text-pri)]">Ramification: </span>
                    <span className="text-[var(--text-sec)]">"{jediInstructions.examples['ramifications']}"</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Post-Draft Checklist — collapsible */}
      {showChecklist && (
        <div className="border-t border-[var(--rule)] bg-[var(--bg-raised)]">
          <button
            onClick={() => setShowChecklistSection(!showChecklistSection)}
            className="flex items-center justify-between w-full px-5 py-3 text-left hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/60 transition-colors"
          >
            <span className="text-xs font-semibold text-[var(--text-sec)]">Post-Draft Checklist</span>
            {showChecklistSection ? (
              <ChevronUp className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            )}
          </button>
          {showChecklistSection && (
            <div className="px-5 pb-4 space-y-4">
              {[
                postDraftChecklist.structural,
                postDraftChecklist.quality,
                postDraftChecklist.complementary,
                postDraftChecklist.jedi,
              ].map((section, idx) => (
                <div key={idx}>
                  <h4 className="text-xs font-semibold text-[var(--text-sec)] mb-2">{section.title}</h4>
                  {'sections' in section ? (
                    (section as typeof postDraftChecklist.structural).sections.map((sub, si) => (
                      <div key={si} className="mb-2">
                        <h5 className="text-xs font-medium text-[var(--text-sec)] mb-1">{sub.title}</h5>
                        <ul className="space-y-1 pl-3 border-l-2 border-primary-200 dark:border-primary-800">
                          {sub.checks.map((check, ci) => (
                            <li key={ci} className="text-xs text-[var(--text-sec)] flex items-start gap-1.5">
                              <span className="text-primary-500 dark:text-primary-400 mt-0.5 flex-shrink-0">□</span>
                              <span>{check}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))
                  ) : (
                    <ul className="space-y-1 pl-3 border-l-2 border-primary-200 dark:border-primary-800">
                      {(section as { title: string; checks: string[] }).checks.map((check, ci) => (
                        <li key={ci} className="text-xs text-[var(--text-sec)] flex items-start gap-1.5">
                          <span className="text-primary-500 dark:text-primary-400 mt-0.5 flex-shrink-0">□</span>
                          <span>{check}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
